"use client";
import config from "@/config/config";
import socketService from "@/services/socketService";

export const COOKIE_NAMES = {
  TOKEN: "token",
  USER: "userData",
};

export function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getUserFromCookies() {
  const userData = getCookie(COOKIE_NAMES.USER);
  if (!userData) return null;
  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
}

export function isAuthenticated() {
  if (typeof document === "undefined") return false;
  return document.cookie.includes(`${COOKIE_NAMES.TOKEN}=`);
}

// Only store token in cookie - user data will be fetched and stored by middleware
export function setAuthToken(token) {
  if (!token) {
    console.error("setAuthToken: Missing token");
    return;
  }
  
  const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
  const isSecure = window.location.protocol === 'https:';
  const cookieOptions = `expires=${expires}; path=/; sameSite=${isSecure ? 'none' : 'lax'}${isSecure ? '; secure' : ''}`;
  
  // Store only token - user data will be fetched by middleware
  document.cookie = `${COOKIE_NAMES.TOKEN}=${token}; ${cookieOptions}`;
}

// For backward compatibility and manual updates (like profile update)
export function setAuthData(user, token) {
  if (!token) {
    console.error("setAuthData: Missing token");
    return;
  }
  
  const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
  const isSecure = window.location.protocol === 'https:';
  const cookieOptions = `expires=${expires}; path=/; sameSite=${isSecure ? 'none' : 'lax'}${isSecure ? '; secure' : ''}`;
  
  // Store both token and user data in cookies
  document.cookie = `${COOKIE_NAMES.TOKEN}=${token}; ${cookieOptions}`;
  if (user) {
    document.cookie = `${COOKIE_NAMES.USER}=${encodeURIComponent(JSON.stringify(user))}; ${cookieOptions}`;
  }
}

export function clearAuthData() {
  if (socketService?.disconnect) socketService.disconnect();
  
  const past = new Date(0).toUTCString();
  document.cookie = `${COOKIE_NAMES.TOKEN}=; expires=${past}; path=/`;
  document.cookie = `${COOKIE_NAMES.USER}=; expires=${past}; path=/`;
  
  if (typeof window !== 'undefined') {
    localStorage.clear();
    sessionStorage.clear();
  }
}

// Fetch user data from backend using token
export async function fetchUserData(token) {
  try {
    const response = await fetch(
      `${config.NEXT_PUBLIC_API_URL}/api/website-user/me`,
      {
        method: "GET",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json" 
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch user data");
    }

    const data = await response.json();
    if (data.success && data.data) {
      return { success: true, user: data.data };
    }
    throw new Error("Invalid response format");
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function login(credentials) {
  try {
    const response = await fetch(
      `${config.NEXT_PUBLIC_API_URL}/api/public/auth/login`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
        credentials: "include",
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Login failed");
    }

    const data = await response.json();
    if (!data.data?.token) throw new Error("No token received");

    // Store only token - user data will be fetched by middleware
    setAuthToken(data.data.token);
    
    // Fetch user data immediately after login
    const userResult = await fetchUserData(data.data.token);
    if (!userResult.success) {
      throw new Error("Failed to fetch user data");
    }
    
    // Store user data in cookie
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
    const isSecure = window.location.protocol === 'https:';
    const cookieOptions = `expires=${expires}; path=/; sameSite=${isSecure ? 'none' : 'lax'}${isSecure ? '; secure' : ''}`;
    document.cookie = `${COOKIE_NAMES.USER}=${encodeURIComponent(JSON.stringify(userResult.user))}; ${cookieOptions}`;
    
    if (socketService?.connect) {
      socketService.connect(data.data.token);
    }

    return { success: true, data: { token: data.data.token, user: userResult.user } };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function logout() {
  try {
    await fetch(`${config.NEXT_PUBLIC_API_URL}/api/admin/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  } catch {
    // Ignore
  }
  
  clearAuthData();
  return { success: true };
}
